import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import VideoStatus from './VideoStatus';
import { getCarflixMedia } from '../helpers/videos';

// Resolves once the browser has left picture-in-picture, or null if it was not active.
const exitPictureInPicture = (player) => {
	if (
		player &&
		typeof player.isInPictureInPicture === 'function' &&
		player.isInPictureInPicture()
	) {
		return Promise.resolve(player.exitPictureInPicture()).catch(() => {});
	}
	return null;
};

const CarflixVideoPlayer = ({ video, isVisible, isModalOpen }) => {
	const videoRef = useRef(null);
	const playerRef = useRef(null);
	const { id, uri } = video || {};
	const { src, poster } = getCarflixMedia(uri);

	const [isLoading, setIsLoading] = useState(false);
	// Message comes straight from Video.js (or the thrown error if the player fails to load).
	const [errorMessage, setErrorMessage] = useState(null);

	useEffect(() => {
		let destroy = null;
		let cancelled = false;

		setIsLoading(false);
		setErrorMessage(null);

		import('@atc/videojs-player')
			.then(({ createPlayer, destroyPlayer }) => {
				if (cancelled || !videoRef.current) return;

				destroy = destroyPlayer;
				const player = createPlayer(videoRef.current, {
					src,
					image: poster,
					videoId: id ? `${id}` : undefined
				});
				playerRef.current = player;

				player.ready(() => player.volume(1));

				const onLoading = () => setIsLoading(true);
				const onReady = () => setIsLoading(false);
				const onError = () => {
					const error = player.error && player.error();
					setIsLoading(false);
					setErrorMessage((error && error.message) || '');
				};

				let hasStarted = false;
				player.on('play', () => {
					if (!hasStarted) onLoading();
				});
				player.on('playing', () => {
					hasStarted = true;
					onReady();
				});
				player.on(['waiting', 'seeking'], onLoading);
				player.on(['canplay', 'seeked'], () => {
					if (hasStarted) onReady();
				});
				player.on(['pause', 'ended'], onReady);
				player.on('error', onError);
			})
			.catch((error) => {
				if (cancelled) return;
				setIsLoading(false);
				setErrorMessage((error && error.message) || '');
			});

		return () => {
			cancelled = true;
			const player = playerRef.current;
			playerRef.current = null;
			if (!player) return;

			// Wait for the browser to leave picture-in-picture before disposing the video element.
			const exiting = exitPictureInPicture(player);
			if (!destroy) return;
			if (exiting) {
				exiting.then(() => destroy(player));
			} else {
				destroy(player);
			}
		};
	}, [src, poster, id]);

	// Pause when the user switches back to photos or closes the lightbox.
	useEffect(() => {
		const player = playerRef.current;
		if (!isVisible && player && typeof player.pause === 'function') {
			player.pause();
		}
	}, [isVisible]);

	// Closing the lightbox also closes picture-in-picture.
	useEffect(() => {
		if (!isModalOpen) {
			exitPictureInPicture(playerRef.current);
		}
	}, [isModalOpen]);

	// Without a message, keep the player visible so Video.js's own error overlay shows.
	const hasError = Boolean(errorMessage);

	return (
		<div className="videojs-container">
			<VideoStatus errorMessage={hasError ? errorMessage : ''} />
			<div className={`videojs-frame${hasError ? ' d-none' : ''}`}>
				<VideoStatus isLoading={isLoading} />
				{/* Video.js moves the <video> into its own wrapper, so keep it inside a React-owned div */}
				<div>
					{/* eslint-disable-next-line jsx-a11y/media-has-caption */}
					<video
						ref={videoRef}
						className="video-js"
						controls
						preload="metadata"
						data-testid="videojs-player"
					/>
				</div>
			</div>
		</div>
	);
};

CarflixVideoPlayer.propTypes = {
	video: PropTypes.shape({
		id: PropTypes.number,
		uri: PropTypes.string
	}),
	isVisible: PropTypes.bool,
	isModalOpen: PropTypes.bool
};

CarflixVideoPlayer.defaultProps = {
	video: {},
	isVisible: false,
	isModalOpen: true
};

export default CarflixVideoPlayer;
